import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plane, Calendar, MapPin, Star, Building2, PartyPopper, Phone, Mail, MapPinned, Menu, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Plane: Plane,
  Calendar: Calendar,
  MapPin: MapPin,
  Star: Star,
  Building2: Building2,
  PartyPopper: PartyPopper,
};

const HomePage = () => {
  const [content, setContent] = useState(null);
  const [services, setServices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    passengers: '',
    vehicle_preference: '',
    pickup_location: '',
    dropoff_location: '',
    date: '',
    time: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`);
        
        const [contentRes, servicesRes, vehiclesRes] = await Promise.all([
          axios.get(`${API}/content`),
          axios.get(`${API}/services`),
          axios.get(`${API}/vehicles`)
        ]);
        setContent(contentRes.data);
        setServices(servicesRes.data);
        setVehicles(vehiclesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, formData);
      setSubmitted(true);
      setFormData({
        name: '', email: '', phone: '', service_type: '', passengers: '',
        vehicle_preference: '', pickup_location: '', dropoff_location: '', date: '', time: '', message: ''
      });
    } catch (error) {
      console.error('Error submitting quote:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    }
    setSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (!content) {
    return (
      <div className="min-h-screen bg-[#020202] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="noise-bg min-h-screen bg-[#020202]">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="text-2xl font-bold text-gold" data-testid="header-logo">
            <span className="font-['Playfair_Display']">YENO</span>
            <span className="font-light text-white/80 ml-2">Transport</span>
          </a>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-sm uppercase tracking-widest text-white/70 hover:text-[#D4AF37] transition-colors" data-testid="nav-services">Services</a>
            <a href="#fleet" className="text-sm uppercase tracking-widest text-white/70 hover:text-[#D4AF37] transition-colors" data-testid="nav-fleet">Flotte</a>
            <a href="#contact" className="text-sm uppercase tracking-widest text-white/70 hover:text-[#D4AF37] transition-colors" data-testid="nav-contact">Contact</a>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="md:hidden bg-[#0A0A0A] border-t border-white/10 px-6 py-4 space-y-4">
            <a href="#services" className="block text-sm uppercase tracking-widest text-white/70" onClick={() => setMobileMenuOpen(false)}>Services</a>
            <a href="#fleet" className="block text-sm uppercase tracking-widest text-white/70" onClick={() => setMobileMenuOpen(false)}>Flotte</a>
            <a href="#contact" className="block text-sm uppercase tracking-widest text-white/70" onClick={() => setMobileMenuOpen(false)}>Contact</a>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-end" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${content.hero_image_url || 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?q=85&w=2070&auto=format&fit=crop'})` }}
        >
          <div className="hero-gradient absolute inset-0"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 w-full">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up opacity-0 font-['Playfair_Display']" data-testid="hero-title">
              {content.hero_title}
            </h1>
            <p className="text-xl text-white/70 mb-10 animate-fade-in-up opacity-0 delay-200" data-testid="hero-subtitle">
              {content.hero_subtitle}
            </p>
            <a 
              href="#contact" 
              className="btn-gold inline-block animate-fade-in-up opacity-0 delay-400"
              data-testid="hero-cta"
            >
              {content.hero_cta_text}
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-6" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 font-['Playfair_Display'] text-gold" data-testid="services-title">
              {content.services_title}
            </h2>
            <p className="text-white/60 text-lg" data-testid="services-subtitle">{content.services_subtitle}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Star;
              return (
                <div 
                  key={service.id} 
                  className={`service-card card-dark p-8 animate-fade-in-up opacity-0 delay-${(index + 1) * 100}`}
                  data-testid={`service-card-${index}`}
                >
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                    <IconComponent className="text-[#D4AF37]" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 font-['Playfair_Display']">{service.title}</h3>
                  <p className="text-white/60">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section id="fleet" className="py-24 px-6 bg-[#0A0A0A]" data-testid="fleet-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 font-['Playfair_Display'] text-gold" data-testid="fleet-title">
              {content.fleet_title}
            </h2>
            <p className="text-white/60 text-lg" data-testid="fleet-subtitle">{content.fleet_subtitle}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {vehicles.map((vehicle, index) => (
              <div 
                key={vehicle.id} 
                className={`vehicle-card text-center animate-fade-in-up opacity-0 delay-${(index + 1) * 100}`}
                data-testid={`vehicle-card-${index}`}
              >
                <div className="relative overflow-hidden mb-6 bg-[#121212] aspect-video">
                  <img 
                    src={vehicle.image_url} 
                    alt={vehicle.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1567788701545-850832a506b8?crop=entropy&cs=srgb&fm=jpg&q=85';
                    }}
                  />
                </div>
                <span className="label-gold">{vehicle.capacity}</span>
                <h3 className="text-2xl font-bold mb-2 font-['Playfair_Display']">{vehicle.name}</h3>
                <p className="text-white/60">{vehicle.type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact/Quote Section */}
      <section id="contact" className="py-24 px-6" data-testid="contact-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4 font-['Playfair_Display'] text-gold" data-testid="contact-title">
                {content.contact_title}
              </h2>
              <p className="text-white/60 text-lg mb-10" data-testid="contact-subtitle">{content.contact_subtitle}</p>
              
              {submitted ? (
                <div className="card-dark p-8 text-center" data-testid="quote-success">
                  <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Star className="text-[#D4AF37]" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 font-['Playfair_Display']">Demande envoyée !</h3>
                  <p className="text-white/60 mb-6">Nous vous contacterons dans les plus brefs délais.</p>
                  <button 
                    onClick={() => setSubmitted(false)} 
                    className="btn-outline"
                    data-testid="new-quote-btn"
                  >
                    Nouvelle demande
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-gold">Nom complet *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="input-dark w-full"
                        placeholder="Votre nom"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="label-gold">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="input-dark w-full"
                        placeholder="votre@email.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-gold">Téléphone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="input-dark w-full"
                        placeholder="+33 6 XX XX XX XX"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="label-gold">Type de service *</label>
                      <select
                        name="service_type"
                        value={formData.service_type}
                        onChange={handleChange}
                        required
                        className="input-dark w-full"
                        data-testid="select-service"
                      >
                        <option value="">Sélectionnez un service</option>
                        <option value="Transport Aéroport">Transport Aéroport</option>
                        <option value="Événements Privés">Événements Privés</option>
                        <option value="Longue Distance">Longue Distance</option>
                        <option value="Chauffeur à Disposition">Chauffeur à Disposition</option>
                        <option value="Transport d'Affaires">Transport d'Affaires</option>
                        <option value="Occasions Spéciales">Occasions Spéciales</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-gold">Nombre de passagers *</label>
                      <select
                        name="passengers"
                        value={formData.passengers}
                        onChange={handleChange}
                        required
                        className="input-dark w-full"
                        data-testid="select-passengers"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="1">1 passager</option>
                        <option value="2">2 passagers</option>
                        <option value="3">3 passagers</option>
                        <option value="4">4 passagers</option>
                        <option value="5-7">5-7 passagers</option>
                        <option value="8-9">8-9 passagers</option>
                      </select>
                    </div>
                    <div>
                      <label className="label-gold">Véhicule préféré</label>
                      <select
                        name="vehicle_preference"
                        value={formData.vehicle_preference}
                        onChange={handleChange}
                        className="input-dark w-full"
                        data-testid="select-vehicle"
                      >
                        <option value="">Aucune préférence</option>
                        <option value="Ford Mondeo">Ford Mondeo</option>
                        <option value="Mercedes Classe V">Mercedes Classe V</option>
                        <option value="Trafic 9 places">Trafic 9 places</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-gold">Lieu de prise en charge *</label>
                      <input
                        type="text"
                        name="pickup_location"
                        value={formData.pickup_location}
                        onChange={handleChange}
                        required
                        className="input-dark w-full"
                        placeholder="Adresse de départ"
                        data-testid="input-pickup"
                      />
                    </div>
                    <div>
                      <label className="label-gold">Destination *</label>
                      <input
                        type="text"
                        name="dropoff_location"
                        value={formData.dropoff_location}
                        onChange={handleChange}
                        required
                        className="input-dark w-full"
                        placeholder="Adresse d'arrivée"
                        data-testid="input-dropoff"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="label-gold">Date *</label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        className="input-dark w-full"
                        data-testid="input-date"
                      />
                    </div>
                    <div>
                      <label className="label-gold">Heure *</label>
                      <input
                        type="time"
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        required
                        className="input-dark w-full"
                        data-testid="input-time"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="label-gold">Message (optionnel)</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="input-dark w-full resize-none"
                      placeholder="Informations complémentaires..."
                      data-testid="input-message"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-gold w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-quote-btn"
                  >
                    {submitting ? 'Envoi en cours...' : 'Demander un devis'}
                  </button>
                </form>
              )}
            </div>
            
            {/* Contact Info */}
            <div className="lg:pl-8">
              <div className="card-dark p-8 mb-8">
                <h3 className="text-2xl font-bold mb-6 font-['Playfair_Display']">Contactez-nous</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="text-[#D4AF37]" size={18} />
                    </div>
                    <div>
                      <span className="label-gold">Téléphone</span>
                      <p className="text-lg">{content.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#D4AF37]" size={18} />
                    </div>
                    <div>
                      <span className="label-gold">Email</span>
                      <p className="text-lg">{content.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <MapPinned className="text-[#D4AF37]" size={18} />
                    </div>
                    <div>
                      <span className="label-gold">Adresse</span>
                      <p className="text-lg">{content.address}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="card-dark p-8">
                <h3 className="text-xl font-bold mb-4 font-['Playfair_Display']">Disponibilité</h3>
                <p className="text-white/60">
                  Service disponible 24h/24, 7j/7. Réservation recommandée 24h à l'avance pour garantir la disponibilité.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10 bg-[#0A0A0A]" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <a href="/" className="text-2xl font-bold text-gold">
                <span className="font-['Playfair_Display']">YENO</span>
                <span className="font-light text-white/80 ml-2">Transport</span>
              </a>
              <p className="text-white/40 mt-2 text-sm">{content.footer_text}</p>
            </div>
            <div className="flex items-center gap-6">
              <a href="#services" className="text-sm text-white/60 hover:text-[#D4AF37] transition-colors">Services</a>
              <a href="#fleet" className="text-sm text-white/60 hover:text-[#D4AF37] transition-colors">Flotte</a>
              <a href="#contact" className="text-sm text-white/60 hover:text-[#D4AF37] transition-colors">Contact</a>
              <a href="/admin" className="text-sm text-white/30 hover:text-white/50 transition-colors" data-testid="footer-admin">Admin</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
